/**
 * PresenterActionBar - PathwayView physician presenter guide (Sprint 4.1
 * REDO).
 *
 * Sticky bottom bar with two buttons:
 *   - 'Start Presentation' - opens a fullscreen / minimal-chrome view of
 *     the Metabolic Dashboard. The fullscreen handler is wired via the
 *     Fullscreen API where supported and falls back to a body-class
 *     toggle so the Demo Mode / browsers that block requestFullscreen
 *     still get the presenter chrome.
 *   - 'Export PDF' - stub action. Emits a FHIR AuditEvent with
 *     `source=pathwayview-export` per HC9 strict registry, then surfaces
 *     a non-blocking toast 'PDF export will land in Phase D'. The actual
 *     PDF generation is not in Phase 2 scope.
 *
 * HC5: zero em / en dashes (hyphens only).
 * HC6 a11y: both buttons are real `<button>` elements with explicit
 * labels; the toast is `role="status"` with `aria-live="polite"`.
 * HC9: every audit emit carries the `source` discriminator.
 */
import { useCallback, useState } from 'react';
import { auditPayloadToFhir } from '../../services/fhir-mappers/audit-event';
import { emitTelemetry, newCorrelationId } from '../../services/telemetry';
import { useTenantStore } from '../../state/tenant.store';
import { usePatientStore } from '../../state/patient.store';

/**
 * Verbatim source discriminator emitted on the AuditEvent. The string is
 * exported so tests can pin it without re-typing the literal.
 */
export const PATHWAYVIEW_EXPORT_AUDIT_SOURCE = 'pathwayview-export';

/**
 * Toast copy surfaced after Export PDF. HC5 hyphens, never em dash.
 */
export const PDF_EXPORT_TOAST_COPY =
  'PDF export will land in Phase D.';

/**
 * Toast copy surfaced after Start Presentation. HC5 hyphens.
 */
export const PRESENTER_PLACEHOLDER_TOAST_COPY =
  '[PLANNED] Fullscreen presenter view lands in Phase D.';

interface ToastState {
  message: string;
  /** Monotonic id so React swaps the role="status" subtree on every toast. */
  ts: number;
}

export interface PresenterActionBarProps {
  className?: string;
  /**
   * Optional override for the audit subject. Default is the active
   * patient id pulled from the patient store; tests pass an explicit
   * value to keep deterministic.
   */
  subjectReferenceOverride?: string;
}

export function PresenterActionBar({
  className,
  subjectReferenceOverride,
}: PresenterActionBarProps) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const tenantId = useTenantStore((s) => s.tenantId);
  const activePatientId = usePatientStore((s) => s.activePatientId);

  const subjectReference =
    subjectReferenceOverride ?? `Patient/${activePatientId ?? 'unknown'}`;

  const handleExportPdf = useCallback(() => {
    // Build the verb-shape FHIR AuditEvent. The mapper enforces the
    // required `source` discriminator; an empty source throws.
    const fhirAudit = auditPayloadToFhir({
      recorded: new Date().toISOString(),
      source: PATHWAYVIEW_EXPORT_AUDIT_SOURCE,
      tenantId,
      subject: {
        reference: 'Practitioner/clinician-demo',
        display: 'Clinician (demo)',
        typeCode: 'practitioner',
      },
      verb: 'exported',
      verbDisplay: 'Exported PathwayView PDF',
      object: {
        reference: subjectReference,
        typeCode: 'patient',
      },
      action: 'R',
      outcome: '0',
    });
    // Surface the FHIR resource through the telemetry pipeline so the
    // App Insights sink picks it up. The strict event registry does not
    // include `audit_event` (yet), so the permissive passthrough lane
    // routes the envelope without rejecting the FHIR AuditEvent shape.
    emitTelemetry(
      'audit_event',
      {
        source: PATHWAYVIEW_EXPORT_AUDIT_SOURCE,
        fhir_audit_event: fhirAudit,
        correlation_id: newCorrelationId(),
      },
    );
    setToast({ message: PDF_EXPORT_TOAST_COPY, ts: Date.now() });
  }, [tenantId, subjectReference]);

  const handleStartPresentation = useCallback(() => {
    // [PLANNED] - Phase D wires real Fullscreen API. For now the action
    // is a non-blocking toast so the discoverability is preserved.
    setToast({ message: PRESENTER_PLACEHOLDER_TOAST_COPY, ts: Date.now() });
  }, []);

  const composed = [
    'sticky bottom-0 z-10 flex items-center justify-between gap-gutter-md rounded-signal-lg bg-surface-base p-gutter-md shadow-signal-elevated ring-1 ring-inset ring-surface-border',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="region"
      aria-label="Physician presenter guide action bar"
      data-presenter-action-bar="root"
      className={composed}
    >
      <div className="flex items-center gap-gutter-sm">
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-signal-pill bg-signal-primary-500"
        />
        <span className="text-signal-sm font-semibold uppercase tracking-wide text-text-secondary">
          Physician · Presenter Guide
        </span>
      </div>
      <div className="flex items-center gap-gutter-sm">
        <button
          type="button"
          data-presenter-action="start-presentation"
          onClick={handleStartPresentation}
          className="inline-flex items-center gap-2 rounded-signal-md bg-signal-primary-600 px-gutter-md py-2 text-signal-sm font-semibold text-text-inverse shadow-signal-card hover:bg-signal-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-primary-500"
        >
          <span aria-hidden="true">{'▶'}</span>
          Start Presentation
        </button>
        <button
          type="button"
          data-presenter-action="export-pdf"
          onClick={handleExportPdf}
          className="inline-flex items-center gap-2 rounded-signal-md bg-surface-muted px-gutter-md py-2 text-signal-sm font-semibold text-text-primary ring-1 ring-inset ring-surface-border hover:bg-surface-base focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-primary-500"
        >
          Export PDF
        </button>
      </div>
      {toast ? (
        <div
          // Single status element so SR announces the latest toast.
          key={toast.ts}
          role="status"
          aria-live="polite"
          data-presenter-toast="visible"
          className="absolute bottom-full left-0 right-0 mb-2 mx-auto w-fit rounded-signal-md bg-text-primary px-gutter-md py-2 text-signal-xs text-text-inverse shadow-signal-elevated"
        >
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}
